import type { LoginProvider } from '@/enums/login';
import type { ProfileSpecificLink } from '@/@types/graphql-types';
import type {
	UserJwConsentsFragment,
	UserUcConsentsFragment,
} from '@/features/user/graphql/queries/GetUserSettings.query';

export const EMAIL_HASH_KEY = 'hachee';

export const CONSENT_PARTNERS = {
	google: { name: 'Google', short: 'G', id: '1' },
	facebook: { name: 'Facebook', short: 'FB', id: '2' },
	twitter: { name: 'Twitter', short: 'TW', id: '4' },
	tiktok: { name: 'TikTok', short: 'TT', id: '6' },
	snapchat: { name: 'Snapchat', short: 'SC', id: '8' },
	allother: { name: 'All Other Providers', short: 'AO', id: '5' },
	emails: { name: 'Email', short: 'E', id: '7' },
	termsAndConditions: { name: 'Terms and Conditions', short: 'TC', id: '9' },
} as const;

export type Consent = keyof typeof CONSENT_PARTNERS;

export const OnboardingStatus = {
	GLOBAL: 'GLOBAL',
	ONBOARDED: 'ONBOARDED',
} as const;

export type OnboardingStatus = keyof typeof OnboardingStatus;

export type UpdateUserInfoPayload = {
	loginProvider: LoginProvider;
	name: string;
	email: string;
	emailVerified: boolean;
	photoURL: string;
};

export enum BuyBoxFilterOptions {
	ALL = 'all',
	// EXP_PROVIDER_BUYBOX_V2
	MY_SERVICES = 'my_services',
	// EXP_PROVIDER_BUYBOX_V2
	SUBSCRIPTION = 'flatrate',
	FREE = 'free',
	// EXP_LINEAR_TV_2
	// FAST_TV is the pre-redesign (control) filter; LINEAR_TV is the variant_1 redesign filter
	FAST_TV = 'fast',
	LINEAR_TV = 'linear',
	// EXP_LINEAR_TV_2
	BUY = 'buy',
	RENT = 'rent',
	CINEMA = 'cinema',
	PHYSICAL = 'physical',
}

export enum NewBuyBoxView {
	LIST = 'list',
	GRID = 'grid',
}

export interface UserInfo {
	email?: string;
	loginProvider?: LoginProvider;
	name?: string;
	emailVerified?: boolean;
	creationTime?: Date;
	photoURL?: string;
}

export enum premiumPurchaseFlowState {
	AUTH_STARTED = 'AUTH_STARTED',
	AUTH_SUCCESS = 'AUTH_SUCCESS',
	CHECKOUT = 'CHECKOUT',
}

export type UserJwConsents = Omit<UserJwConsentsFragment, '__typename'>;
export type UserUcConsents = Omit<UserUcConsentsFragment, '__typename'>;

export interface UserSettings {
	jwConsents: Record<Consent, boolean | null>;
	providers: string[];
	ucConsents: { id: string; consent: boolean }[] | null;
}

export interface UserSettingsGraphql {
	providers: string[];
	jwConsents: UserJwConsents;
	ucConsents: UserUcConsents[] | null;
}

export interface ProfileInfo {
	uuid: string | null;
	firstName: string | null;
	lastName: string | null;
	city: string | null;
	country: string | null;
	bio: string | null;
	avatarUrl: string | null;
	website: ProfileSpecificLink | null | undefined;
	altWebsite: ProfileSpecificLink | null | undefined;
	instagram: ProfileSpecificLink | null | undefined;
	imdb: ProfileSpecificLink | null | undefined;
	x: ProfileSpecificLink | null | undefined;
	tiktok: ProfileSpecificLink | null | undefined;
	linkedin: ProfileSpecificLink | null | undefined;
	facebook: ProfileSpecificLink | null | undefined;
}

export interface UserSearches {
	recentSearchTerms: string[];
	recentlyVisitedTitleIds: string[];
}

export type UserExperimentsInfo = {
	[experimentName: string]: {
		variant: string | null;
		preferred: boolean;
		activated: boolean;
		affiliationShortId: string | null;
	};
};
